import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core'
import { NavLink } from 'react-router-dom'

import { HOME_PATH } from '../../../configs'
import { getRoleLabel } from '../helpers/organisationFields'
import { USAGE_METRIC, USAGE_METRIC_META, getUsageRatio, isUnlimited } from '../../../constants/usageLimits'
import { SUPERVISION_MODE_LABEL } from '../../../constants/supervisionMode'
import { ROLE } from '../../../security'
import {
  isBotPackageCarePlanEnabled,
  isBotPackageSupervisionEnabled,
} from '../api/usageLimits'
import { getCachedBotPackageAssignment } from '../helpers/orgPackages'
import AssistantStatusModal from './AssistantStatusModal'
import { AssistantColorSwitch } from '../../../uiKit/Switch'

/**
 * Card view of the organisations list.
 *
 * Same rows, same data, same sort as the table — only the shape changes, so an
 * operator switching view never sees a different set of organisations.
 *
 * DESIGN.md compliance:
 *  - §11 `card`: {colors.surface}, {rounded.lg} (20px), {shadows.card}. The
 *    shadow does the separating, so there is no border as well.
 *  - §02 "Colour as Meaning": the monogram is one neutral treatment, not a hash
 *    of the name to a hue — a colour that encodes nothing is noise.
 *  - §02 "Status Is Never Color Alone": every usage bar carries its numbers.
 */

const styles = () => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    // §06 card-row and overview-grid gaps sit at 24px.
    gap: 'var(--careco-space-xl, 24px)',
    width: '100%',
  },
  '@media (max-width: 600px)': {
    grid: {
      gridTemplateColumns: '1fr',
    },
  },
  // §11 `card`: {colors.surface}, {rounded.lg}, {shadows.card}. Elevation does
  // the separating, so there is no border as well — §03, no heavy borders where
  // another device already carries the same separation.
  card: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'var(--careco-rounded-lg, 20px)',
    background: 'var(--careco-surface)',
    boxShadow: 'var(--careco-shadow-card, 0 2px 10px 2px rgba(110,110,110,0.06))',
    overflow: 'hidden',
    transition: 'opacity 120ms ease',
  },
  cardInactive: {
    opacity: 0.55,
  },
  top: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px 20px 14px',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': { background: 'var(--careco-primary-light)' },
    '&:hover $openIcon': { color: 'var(--careco-primary)', transform: 'translateX(2px)' },
    '&:focus-visible': {
      outline: '2px solid var(--careco-primary)',
      outlineOffset: -2,
    },
  },
  // "Open" affordance, pinned to the card's top-right; part of the header link,
  // so clicking it opens the organisation like the rest of the header.
  openIcon: {
    marginLeft: 'auto',
    flexShrink: 0,
    alignSelf: 'flex-start',
    display: 'inline-flex',
    color: 'var(--careco-text-muted)',
    transition: 'color 120ms ease, transform 120ms ease',
  },
  monogram: {
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--careco-divider)',
    color: 'var(--careco-icon-default)',
    fontSize: 13,
    fontWeight: 700,
  },
  identity: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  name: {
    fontFamily: 'var(--careco-font)',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    fontFamily: 'var(--careco-font)',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--careco-text-muted)',
  },
  body: {
    padding: '0 20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  // Label/value rows (Tier, Supervision) — mirroring the table columns.
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    minHeight: 22,
  },
  infoLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--careco-text-secondary)',
  },
  infoValue: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  infoMuted: { color: 'var(--careco-text-muted)', fontWeight: 500 },
  // Account-tier pill — same three treatments as the table.
  tierPill: {
    display: 'inline-block',
    boxSizing: 'border-box',
    maxWidth: 95,
    padding: '4px 12px',
    borderRadius: 'var(--careco-rounded-pill)',
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
  },
  tierGold: { background: 'var(--careco-status-inactive-light)', color: 'var(--careco-status-inactive)' },
  tierPlatinum: { background: 'var(--careco-primary-light)', color: 'var(--careco-primary)' },
  tierBasic: { background: 'var(--careco-divider)', color: 'var(--careco-text-secondary)' },
  // The renewal countdown, right-aligned: coloured days-left on top, the
  // exact renewal date beneath it.
  renewalValue: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },
  renewalText: {
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  // Plenty of runway, worth a heads-up, and due now — the same semantics as
  // the usage meter's default/warn/spent colours, reused rather than invented.
  renewalOk: { color: 'var(--careco-success)' },
  renewalWarn: { color: 'var(--careco-status-inactive)' },
  renewalUrgent: { color: 'var(--careco-error)' },
  renewalDateText: {
    fontSize: 11.5,
    color: 'var(--careco-text-muted)',
    whiteSpace: 'nowrap',
  },
  meterRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  meterHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '8px',
    fontFamily: 'var(--careco-font)',
  },
  meterLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--careco-text-secondary)',
  },
  meterValue: {
    fontSize: 13,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--careco-text-primary)',
    whiteSpace: 'nowrap',
  },
  meterValueSpent: { color: 'var(--careco-error)' },
  meterValueWarn: { color: 'var(--careco-status-inactive)' },
  track: {
    height: 6,
    borderRadius: 'var(--careco-rounded-pill, 999px)',
    background: 'var(--careco-divider)',
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: 'inherit',
    background: 'var(--careco-primary)',
  },
  fillSpent: { background: 'var(--careco-error)' },
  fillWarn: { background: 'var(--careco-status-inactive)' },
  unknown: {
    fontFamily: 'var(--careco-font)',
    fontSize: 12.5,
    color: 'var(--careco-text-muted)',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 20px',
    borderTop: '1px solid var(--careco-divider)',
  },
  statusCell: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  statusDot: {
    width: 8,
    height: 8,
    flexShrink: 0,
    borderRadius: '50%',
    marginRight: 2,
  },
  statusDotOn: { background: 'var(--careco-success)' },
  statusDotOff: { background: 'var(--careco-text-muted)' },
  statusLabel: {
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  statusLabelOn: { color: 'var(--careco-success)' },
  statusLabelOff: { color: 'var(--careco-text-secondary)' },
  manage: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'var(--careco-font)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--careco-primary)',
    textDecoration: 'underline',
  },
  empty: {
    gridColumn: '1 / -1',
    padding: '56px 20px',
    textAlign: 'center',
    borderRadius: 'var(--careco-rounded-lg, 20px)',
    background: 'var(--careco-surface)',
    boxShadow: 'var(--careco-shadow-card, 0 2px 10px 2px rgba(110,110,110,0.06))',
    fontFamily: 'var(--careco-font)',
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    marginBottom: 4,
  },
  emptyBody: {
    fontSize: 14,
    color: 'var(--careco-text-muted)',
  },
})

// Supervision shows the account's mode (Default/Dynamic), not a meter — so
// Care plan audits is the only metered row, matching the table.
const GRID_METRICS = [USAGE_METRIC.CARE_PLAN_AUDITS]

const TIER_LABEL = { GOLD: 'Gold', PLATINUM: 'Platinum', BASIC: 'Basic' }
const TIER_CLASS = { GOLD: 'tierGold', PLATINUM: 'tierPlatinum', BASIC: 'tierBasic' }
// packageTiers.tierLevelFor returns title-case Basic/Gold/Platinum.
const PACKAGE_TIER_CLASS = {
  Basic: 'tierBasic',
  Gold: 'tierGold',
  Platinum: 'tierPlatinum',
}

const RENEWAL_URGENCY_CLASS = {
  ok: 'renewalOk',
  warn: 'renewalWarn',
  urgent: 'renewalUrgent',
}

const OrganisationsGrid = props => {
  const {
    classes,
    rows,
    usageByBotId,
    onOpen,
    onManageLimits,
    onToggleActivation,
    togglingBotId,
    searchTerm,
    hasFilters,
  } = props
  // Limits / activation: only ROLE_ADMIN on that bot.
  const canManageRow = row => row.role === ROLE.ADMIN

  const [pendingRow, setPendingRow] = useState(null)
  const requestActivationToggle = row => setPendingRow(row)
  const closeActivationToggle = () => setPendingRow(null)
  const confirmActivationToggle = () => {
    if (pendingRow && onToggleActivation) {
      onToggleActivation(pendingRow, !Boolean(pendingRow.botActivated))
    }
    setPendingRow(null)
  }

  const renderActivationStatus = row => {
    const active = Boolean(row.botActivated)
    return (
      <span className={classes.statusCell}>
        <span
          className={`${classes.statusDot} ${active ? classes.statusDotOn : classes.statusDotOff}`}
          aria-hidden="true"
        />
        <span
          className={`${classes.statusLabel} ${
            active ? classes.statusLabelOn : classes.statusLabelOff
          }`}>
          {active ? 'Active' : 'Inactive'}
        </span>
      </span>
    )
  }

  const renderTier = row => {
    // An assigned package is the org's tier; style from its feature count when known.
    if (row.packageName) {
      const tierClassKey = PACKAGE_TIER_CLASS[row.packageTier]
      const tierClass = tierClassKey ? classes[tierClassKey] : classes.tierPlatinum
      return (
        <span className={`${classes.tierPill} ${tierClass}`} title={row.packageName}>
          {row.packageName}
        </span>
      )
    }
    if (row.tier && TIER_LABEL[row.tier]) {
      return (
        <span className={`${classes.tierPill} ${classes[TIER_CLASS[row.tier]]}`}>
          {TIER_LABEL[row.tier]}
        </span>
      )
    }
    return <span className={`${classes.infoValue} ${classes.infoMuted}`}>—</span>
  }

  const renderValue = label => {
    if (label) return <span className={classes.infoValue}>{label}</span>
    return <span className={`${classes.infoValue} ${classes.infoMuted}`}>—</span>
  }

  const getRowAssignment = row =>
    row.bot?.packageDetails || getCachedBotPackageAssignment(row.id) || null

  const renderSupervisionMode = row => {
    const assignment = getRowAssignment(row)
    if (assignment && !isBotPackageSupervisionEnabled(assignment)) {
      return renderValue(null)
    }
    return renderValue(SUPERVISION_MODE_LABEL[row.supervisionMode])
  }

  // The renewal cycle: how many days remain (coloured by urgency) and the
  // date it renews on. Deactivated orgs and rows without a renewal date show
  // an em dash — nothing to count down.
  const renderRenewalCycle = row => {
    if (!row.botActivated || !row.renewalInfo) {
      return <span className={`${classes.infoValue} ${classes.infoMuted}`}>—</span>
    }
    return (
      <span className={classes.renewalValue}>
        <span
          className={`${classes.renewalText} ${
            classes[RENEWAL_URGENCY_CLASS[row.renewalInfo.urgency]]
          }`}>
          {row.renewalInfo.daysLeftLabel}
        </span>
        <span className={classes.renewalDateText}>{row.renewalInfo.renewsOnLabel}</span>
      </span>
    )
  }

  const renderMeter = (row, metric) => {
    const meta = USAGE_METRIC_META[metric]
    if (metric === USAGE_METRIC.CARE_PLAN_AUDITS) {
      const assignment = getRowAssignment(row)
      if (assignment && !isBotPackageCarePlanEnabled(assignment)) {
        return (
          <div className={classes.meterRow} key={metric}>
            <div className={classes.meterHead}>
              <span className={classes.meterLabel}>{meta.label}</span>
              <span className={classes.unknown}>—</span>
            </div>
          </div>
        )
      }
    }
    const entry = usageByBotId?.[row.id]?.metrics?.[metric]

    // No reading means no bar. A zeroed bar would read as "nothing used yet",
    // which is a different claim from "we could not check".
    if (!entry || typeof entry.used !== 'number') {
      return (
        <div className={classes.meterRow} key={metric}>
          <div className={classes.meterHead}>
            <span className={classes.meterLabel}>{meta.label}</span>
            <span className={classes.unknown}>—</span>
          </div>
        </div>
      )
    }

    const unlimited = isUnlimited(entry.limit)
    const ratio = getUsageRatio(entry.used, entry.limit)
    const spent = !unlimited && entry.used >= entry.limit
    const warn = !spent && ratio >= 0.8

    // Prefer bot/list carePlanAuditUsagePercentage for the care-plan audits
    // bar; fall back to used/limit when the field is missing.
    const pctFromApi =
      metric === USAGE_METRIC.CARE_PLAN_AUDITS &&
      typeof row.carePlanAuditUsagePercentage === 'number'
        ? Math.round(row.carePlanAuditUsagePercentage)
        : null
    const pct = Math.min(100, Math.max(0, pctFromApi ?? Math.round(ratio * 100)))

    return (
      <div className={classes.meterRow} key={metric}>
        <div className={classes.meterHead}>
          <span className={classes.meterLabel}>{meta.label}</span>
          <span
            className={`${classes.meterValue} ${spent ? classes.meterValueSpent : ''} ${
              warn ? classes.meterValueWarn : ''
            }`}>
            {unlimited ? `${entry.used} used · no limit` : `${entry.used} of ${entry.limit}`}
          </span>
        </div>
        <div className={classes.track}>
          <div
            className={`${classes.fill} ${spent ? classes.fillSpent : ''} ${warn ? classes.fillWarn : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className={classes.grid}>
        <div className={classes.empty}>
          <div className={classes.emptyTitle}>No organisations found</div>
          <div className={classes.emptyBody}>
            {searchTerm
              ? `Nothing matches “${searchTerm}”.`
              : hasFilters
                ? 'No organisation matches the current filters.'
                : 'QCS organisations will appear here once they are onboarded.'}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.grid}>
      {rows.map(row => (
        <div
          className={`${classes.card} ${row.botActivated === false ? classes.cardInactive : ''}`}
          key={row.id}>
          <NavLink
            className={classes.top}
            to={`${HOME_PATH}/bot/${row.id}/${row.landingTab}`}
            onClick={() => onOpen(row.bot)}>
            <span className={classes.monogram} aria-hidden="true">
              {row.initials}
            </span>
            <span className={classes.identity}>
              <span className={classes.name} title={row.name}>
                {row.name}
              </span>
              <span className={classes.meta}>
                {row.role ? getRoleLabel(row.role) : 'No access'} · {row.updatedLabel}
              </span>
            </span>
            <span className={classes.openIcon} aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="m12 5 7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </NavLink>

          <div className={classes.body}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Status</span>
              {renderActivationStatus(row)}
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Renewal cycle</span>
              {renderRenewalCycle(row)}
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Tier</span>
              {renderTier(row)}
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Supervision</span>
              {renderSupervisionMode(row)}
            </div>
            {GRID_METRICS.map(metric => renderMeter(row, metric))}
          </div>

          {(canManageRow(row) && (onToggleActivation || onManageLimits)) && (
            <div className={classes.footer}>
              {canManageRow(row) && onToggleActivation ? (
                <AssistantColorSwitch
                  checked={Boolean(row.botActivated)}
                  disabled={togglingBotId != null && Number(togglingBotId) === Number(row.id)}
                  ariaLabel={row.botActivated ? 'Deactivate organisation' : 'Activate organisation'}
                  onChange={() => requestActivationToggle(row)}
                />
              ) : (
                <span />
              )}
              {canManageRow(row) && Boolean(row.botActivated) && onManageLimits ? (
                <button className={classes.manage} onClick={() => onManageLimits(row)}>
                  Manage limits
                </button>
              ) : null}
            </div>
          )}
        </div>
      ))}
      <AssistantStatusModal
        open={Boolean(pendingRow)}
        activating={Boolean(pendingRow && !pendingRow.botActivated)}
        onCancel={closeActivationToggle}
        onConfirm={confirmActivationToggle}
      />
    </div>
  )
}

OrganisationsGrid.propTypes = {
  classes: PropTypes.object,
  rows: PropTypes.array,
  usageByBotId: PropTypes.object,
  onOpen: PropTypes.func,
  onManageLimits: PropTypes.func,
  onToggleActivation: PropTypes.func,
  togglingBotId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  searchTerm: PropTypes.string,
  hasFilters: PropTypes.bool,
}

export default withStyles(styles)(OrganisationsGrid)
